var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import classNames from 'classnames';
import omit from 'rc-util/lib/omit';
import CloseOutlined from 'acud-icon/lib/icons/OutlinedCloseSmall';
import SizeContext from '../config-provider/SizeContext';
import Popover from '../popover';
import CheckableTag from './CheckableTag';
import TagList from './TagList';
import { ConfigContext } from '../config-provider';
import { PresetStatusTypes } from '../_util/colors';
import { hexToRgb, characterLen } from './util';
export { CheckableTagProps } from './CheckableTag';
export { TagListProps } from './TagList';
const PresetStatusRegex = new RegExp(`^(${PresetStatusTypes.join('|')})(-outline|-enhance|-status)?$`);
const BorderRegex = new RegExp(`^(${PresetStatusTypes.join('|')})(-outline)$`);
const InternalTag = (_a, ref) => {
    var { prefixCls: customizePrefixCls, className, style, children, icon = null, size: customizeSize, color, onClose, closeIcon, type, closable = false } = _a, props = __rest(_a, ["prefixCls", "className", "style", "children", "icon", "size", "color", "onClose", "closeIcon", "type", "closable"]);
    const { getPrefixCls, direction } = React.useContext(ConfigContext);
    const [visible, setVisible] = React.useState(true);
    React.useEffect(() => {
        if ('visible' in props) {
            setVisible(props.visible);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.visible]);
    const isPresetColor = () => {
        if (!color) {
            return false;
        }
        return PresetStatusRegex.test(color);
    };
    const colorVal = color && !isPresetColor() ? color : undefined;
    const isTranslucent = type && type.endsWith('-translucent');
    const isMarketing = type && type === 'marketing';
    const isBusiness = type && type === 'business';
    const backgroundColor = isTranslucent ? hexToRgb(type.split('-')[0], 0.5) : undefined;
    const tagStyle = type && type.length
        ? Object.assign(Object.assign(Object.assign(Object.assign({}, (isMarketing ? { borderBottomColor: colorVal } : {})), (isBusiness ? { backgroundColor: colorVal } : {})), (isTranslucent ? { backgroundColor } : {})), style) : Object.assign({ backgroundColor: colorVal }, style);
    const presetColor = isPresetColor();
    const prefixCls = getPrefixCls('tag', customizePrefixCls);
    const textHidden = children && typeof children === 'string' && characterLen(children) > 20;
    const hasIcon = !!icon;
    const onlyIcon = !!icon && (children === null || children === undefined);
    const size = React.useContext(SizeContext);
    const sizeClassNameMap = { large: 'lg', small: 'sm', middle: 'md' };
    const sizeFullname = customizeSize || size || 'middle';
    const sizeCls = (sizeFullname && sizeClassNameMap[sizeFullname]) || '';
    const isOutline = BorderRegex.test(color);
    const tagClassName = classNames(prefixCls, {
        [`${prefixCls}-${color}`]: presetColor,
        [`${prefixCls}-has-color`]: color && !presetColor,
        [`${prefixCls}-hidden`]: !visible,
        [`${prefixCls}-rtl`]: direction === 'rtl',
        [`${prefixCls}-type-${type}`]: type && type.length,
        [`${prefixCls}-${sizeCls}-${type}`]: type && type.length,
        [`${prefixCls}-${sizeCls}${isOutline ? '-outline' : ''}`]: sizeCls,
        [`${prefixCls}${sizeCls ? '-' + sizeCls : ''}-text-hidden`]: textHidden,
        [`${prefixCls}${sizeCls ? '-' + sizeCls : ''}-has-icon`]: hasIcon,
        [`${prefixCls}${sizeCls ? '-' + sizeCls : ''}-only-icon`]: onlyIcon,
    }, className);
    const handleCloseClick = (e) => {
        e.stopPropagation();
        onClose === null || onClose === void 0 ? void 0 : onClose(e);
        if (e.defaultPrevented) {
            return;
        }
        if (!('visible' in props) && !props.disabled) {
            setVisible(false);
        }
    };
    const renderCloseIcon = () => {
        if (closable) {
            return closeIcon ? (<span className={`${prefixCls}-close-icon`} onClick={handleCloseClick}>
                    {closeIcon}
                </span>) : (<CloseOutlined className={`${prefixCls}-close-icon`} onClick={handleCloseClick}/>);
        }
        return null;
    };
    const texts = textHidden ? (<Popover content={children} overlayClassName={`${prefixCls}-text-popover`}>
            <span className="text-hidden">{children}</span>
        </Popover>) : (<span>{children}</span>);
    const tagProps = omit(props, ['visible']);
    const iconNode = icon || null;
    const kids = iconNode ? (<>
            {iconNode}
            {texts}
        </>) : (texts);
    return (<span {...tagProps} ref={ref} className={tagClassName} style={tagStyle}>
            <span className={`${prefixCls}-render-container`}>
                {kids}
                {renderCloseIcon()}
            </span>
        </span>);
};
const Tag = React.forwardRef(InternalTag);
Tag.displayName = 'Tag';
Tag.CheckableTag = CheckableTag;
Tag.TagList = TagList;
export default Tag;
